import React from 'react';
import {Chip, Stack} from '@mui/material';
import {designSystemToken} from '@lightricks/react-design-system';
import translate from '@/utils/translate';
import styles from './CampaignStatusChip.module.scss';

type ChipTone = 'success' | 'neutral';

// Maps backend campaign.state values to a user-facing label and a chip color.
// "Live" uses success-green; everything else uses a neutral outlined chip.
const STATE_TO_CHIP: Record<string, {labelKey: string; tone: ChipTone}> = {
  accepting_proposals: {labelKey: 'Live', tone: 'success'},
  closed_to_new_proposals: {labelKey: 'Live', tone: 'success'},
  draft: {labelKey: 'Draft', tone: 'neutral'},
  under_construction: {labelKey: 'Draft', tone: 'neutral'},
  under_review: {labelKey: 'Under Review', tone: 'neutral'},
  approved: {labelKey: 'Approved', tone: 'success'},
  closed: {labelKey: 'Completed', tone: 'neutral'},
};

interface CampaignStatusChipProps {
  testID?: string;
  state: string;
}

function StatusChip({
  testID,
  labelKey,
  tone,
}: {
  testID: string;
  labelKey: string;
  tone: ChipTone;
}) {
  const isSuccess = tone === 'success';

  return (
    <Chip
      data-testid={testID}
      label={translate(labelKey)}
      size="small"
      variant="outlined"
      sx={{
        'height': '24px',
        'borderRadius': '100px',
        'borderColor': isSuccess
          ? designSystemToken('semantic.fg.success')
          : designSystemToken('semantic.fg.tertiary'),
        'color': isSuccess
          ? designSystemToken('semantic.fg.success')
          : designSystemToken('semantic.fg.primary'),
        'fontFamily': 'inherit',
        'fontWeight': 500,
        'fontSize': '14px',
        'letterSpacing': '0.16px',
        'backgroundColor': 'transparent',
        '& .MuiChip-label': {
          padding: '0 10px',
        },
      }}
    />
  );
}

export default function CampaignStatusChip({
  testID = 'campaign-status-chip',
  state,
}: CampaignStatusChipProps) {
  const config = STATE_TO_CHIP[state] ?? {labelKey: state, tone: 'neutral'};

  const isPaused = state === 'closed_to_new_proposals';

  return (
    <Stack className={styles.chipRow}>
      <StatusChip
        testID={testID}
        labelKey={config.labelKey}
        tone={config.tone}
      />
      {isPaused ? (
        <StatusChip
          testID="campaign-status-chip-paused"
          labelKey="Paused"
          tone="neutral"
        />
      ) : null}
    </Stack>
  );
}
